import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import axios from 'axios';
import apiClient, { authAPI } from '../services/api';
import Icon from '../components/Icon';
import styles from './SubscriptionExpiredPage.module.css';

export default function SubscriptionExpiredPage() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    fetchPlans();
    
    // Polling para verificar si el plan ha sido activado por el webhook de MP o el admin
    const interval = setInterval(async () => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        try {
          const res = await authAPI.verify(currentToken);
          if (res.success && res.doctor && res.doctor.subscription_status === 'active') {
            await refreshUser();
            navigate('/dashboard');
          }
        } catch (e) {
          // ignore
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [navigate, refreshUser]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002'}/api/admin/public/plans`);
      if (res.data.success) {
        const preferredOrder = { basico: 1, commission: 2, mensual_pro: 3, clinica: 4 };
        const ordered = [...(res.data.plans || [])].sort((a, b) =>
          (preferredOrder[a.key] || 99) - (preferredOrder[b.key] || 99)
        );
        setPlans(ordered);
      }
    } catch (e) {
      console.error('Error fetching plans:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleRequestPlan = async (planId) => {
    if (!window.confirm('¿Confirmas que deseas enviar la solicitud para activar este plan? El administrador revisará tu solicitud.')) return;
    try {
      setSubmitting(true);
      const response = await apiClient.post('/api/doctor/subscriptions/request', { pricing_plan_id: planId });
      if (response.data.success) {
        setRequestSent(true);
        alert('Solicitud enviada correctamente. Tu cuenta se activará en cuanto el administrador la apruebe.');
      }
    } catch (err) {
      console.error('Error al solicitar plan:', err);
      alert(err.response?.data?.message || 'Error al enviar la solicitud.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayPlan = async (planId) => {
    try {
      setSubmitting(true);
      const response = await apiClient.post('/api/doctor/subscriptions/mercadopago-preference', { pricing_plan_id: planId });
      if (response.data.success && response.data.initPoint) {
        window.location.href = response.data.initPoint;
      } else {
        alert('Error al generar la preferencia de pago.');
      }
    } catch (err) {
      console.error('Error en pago de plan:', err);
      alert(err.response?.data?.message || 'Error al procesar el pago.');
    } finally {
      setSubmitting(false);
    }
  };

  const getPlanFeatures = (p) => {
    if (Array.isArray(p.features) && p.features.length > 0) {
      return p.features;
    }
    if (typeof p.features === 'string' && p.features.trim()) {
      try {
        const parsed = JSON.parse(p.features);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {
        const split = p.features.split(',').map(s => s.trim()).filter(Boolean);
        if (split.length > 0) return split;
      }
    }
    
    const list = [];
    if (p.allow_patient_booking !== false) list.push('Portal de Reservas de Pacientes');
    if (p.allow_mercadopago !== false) list.push('Cobros de Señas con Mercado Pago');
    if (p.allow_google_calendar !== false) list.push('Sincronización con Google Calendar');
    if (p.allow_reminders !== false) list.push('Recordatorios automáticos');
    if (p.allow_insurance !== false) list.push('Gestión de Obras Sociales');
    if (p.allow_telemedicine) list.push('Consultas Online / Telemedicina');
    if (list.length === 0) {
      list.push('Gestión completa de turnos y pacientes', 'Soporte técnico preferencial');
    }
    return list;
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.iconWrapper}>
          <div className={styles.icon}><Icon name="lock" size={30} color="#64748b" /></div>
        </div>

        <h1 className={styles.title}>Tu Cuenta ha Expirado</h1>
        
        <div className={styles.message}>
          <p className={styles.highlight}>
            Tu acceso a TurnoHub se encuentra suspendido temporalmente por vencimiento de suscripción.
          </p>
          <p>
            Elige uno de nuestros planes comerciales a continuación para reactivar tu cuenta de inmediato o enviar una solicitud al administrador.
          </p>
        </div>

        {requestSent && (
          <div className={styles.alertSuccess}>
            <strong>¡Solicitud Pendiente!</strong> Ya hemos enviado tu solicitud al administrador. Tu cuenta se reactivará automáticamente una vez aprobada.
          </div>
        )}

        <div className={styles.plansSection}>
          <h2>Planes Disponibles</h2>
          {loading ? (
            <p>Cargando planes comerciales...</p>
          ) : (
            <div className={styles.plansGrid}>
              {plans.map(p => {
                const featureList = getPlanFeatures(p);
                const isRecommended = p.key === 'mensual_pro' || p.is_popular;
                return (
                  <div key={p.id} className={`${styles.planCard} ${isRecommended ? styles.popular : ''}`}>
                    {isRecommended && <span className={styles.popularLabel}>Más elegido</span>}
                    <h3>{p.name}</h3>
                    <div className={styles.price}>
                      <span className={styles.amount}>
                        {(() => {
                          if (p.price === null || p.price === undefined || p.price === '') return 'Consultar';
                          const str = String(p.price).trim();
                          if (str.includes('%')) return str;
                          const num = parseFloat(str);
                          if (!isNaN(num)) return `$${num.toLocaleString('es-AR')}`;
                          return str.startsWith('$') ? str : `$${str}`;
                        })()}
                      </span>
                      <span className={styles.period}>/ {p.price_period === 'monthly' ? 'mes' : (p.price_period || 'mes')}</span>
                    </div>
                    <p className={styles.planDesc}>{p.description}</p>
                    
                    {featureList.length > 0 && (
                      <div className={styles.featuresWrapper}>
                        <p className={styles.featuresTitle}>Incluye:</p>
                        <ul className={styles.featuresList}>
                          {featureList.map((f, i) => (
                            <li key={i}>
                              <span className={styles.checkIcon} aria-hidden="true">•</span>
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className={styles.planActions}>
                      <button
                        className={`${styles.payBtn} ${isRecommended ? styles.primaryPayBtn : ''}`}
                        onClick={() => handlePayPlan(p.id)}
                        disabled={submitting}
                      >
                        Pagar Online
                      </button>
                      <button
                        className={styles.requestBtn}
                        onClick={() => handleRequestPlan(p.id)}
                        disabled={submitting}
                      >
                        Solicitar Habilitación Manual
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sección de contacto por WhatsApp */}
        <div className={styles.contactCard}>
          <div className={styles.contactInfo}>
            <div className={styles.wppIconBadge}>
              <Icon name="whatsapp" size={26} color="#25D366" />
            </div>
            <div className={styles.contactDetails}>
              <h4>¿Tienes dudas o necesitas ayuda?</h4>
              <p>Contactate con nosotros para más información sobre planes y promociones.</p>
            </div>
          </div>
          <a
            href="https://wa.me/5493765409032?text=Hola%20TurnoHub,%20quisiera%20más%20información%20sobre%20los%20planes%20de%20suscripción."
            target="_blank"
            rel="noopener noreferrer"
            className={styles.contactWppBtn}
          >
            <Icon name="whatsapp" size={18} color="#FFFFFF" />
            <span>Contactar por WhatsApp</span>
          </a>
        </div>

        <div className={styles.footerActions}>
          <button
            className={styles.logoutBtn}
            onClick={handleLogout}
          >
            Cerrar Sesión
          </button>
        </div>
      </div>
    </div>
  );
}
